import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setApiTokenProvider } from '@/lib/api';
import type { AuthAdapter, AuthUser } from './types';

export type AuthStatus = 'loading' | 'signedIn' | 'signedOut';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  signIn: AuthAdapter['signIn'];
  register: AuthAdapter['register'];
  signInWithGoogle: AuthAdapter['signInWithGoogle'];
  resetPassword: AuthAdapter['resetPassword'];
  sendEmailVerification: AuthAdapter['sendEmailVerification'];
  reloadUser: AuthAdapter['reloadUser'];
  signOut: () => Promise<void>;
  getIdToken: AuthAdapter['getIdToken'];
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ adapter, children }: { adapter: AuthAdapter; children: ReactNode }) {
  const [state, setState] = useState<{ status: AuthStatus; user: AuthUser | null }>({
    status: 'loading',
    user: null,
  });
  const qc = useQueryClient();

  useEffect(() => {
    setApiTokenProvider((force) => adapter.getIdToken(force));
    return adapter.subscribe((user) => {
      setState((prev) => {
        // A different (or no) user: drop every cached query so nothing leaks across accounts.
        if (prev.user?.uid !== user?.uid) qc.clear();
        return { status: user ? 'signedIn' : 'signedOut', user };
      });
    });
  }, [adapter, qc]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn: adapter.signIn,
      register: adapter.register,
      signInWithGoogle: adapter.signInWithGoogle,
      resetPassword: adapter.resetPassword,
      sendEmailVerification: adapter.sendEmailVerification,
      reloadUser: adapter.reloadUser,
      getIdToken: adapter.getIdToken,
      signOut: async () => {
        await adapter.signOut();
        qc.clear();
      },
    }),
    [state, adapter, qc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
